import { http } from '../../../configuration/api';
import moment from 'moment';
import {
	APIResponse,
	CropDataResponse,
	ExpressOffer,
	LanguageDataResponse,
	OffersListResponse,
	OfferStatus,
	RequestDetailResponse,
	SendOfferRequest,
	ServiceDataResponse,
	UserDataResponse,
} from '../types';

// Re-export the SendOfferRequest type to maintain backward compatibility
export type { SendOfferRequest } from '../types';

const MODULE_NAME = 'requests/v1/service-requests';

export const getOffers = async (
	status: OfferStatus = 'active',
	page: number = 1,
	limit: number = 10
): Promise<OffersListResponse> => {
	try {
		const response = await http.get<APIResponse>(
			'requests',
			`/service-requests/express?page=${page}&limit=${limit}&status=${status}`
		);

		console.log('API Response (Specialist Express):', response.data);

		// Map the API response to our internal data structure
		const mappedData = response.data.data.data.map((item: any) => {
			// Log each item's payment status and end_date for debugging
			console.log(`API response item ${item.id}:`);
			console.log(`- payment_status: ${item.payment_status}`);
			console.log(`- end_date: ${item.end_date}`);
			console.log(`- start_date: ${item.start_date}`);
			
			return {
				...item,
				service: {
					name: item.services?.name,
					price: item.services?.price,
					delivery_date: item.end_date,
				},
				is_paid: item.payment_status === 'paid' || item.payment_status === 'stripe_payed',
				payment_status: item.payment_status || 'unpaid', // Ensure payment_status is set
				request_active_for_days: item.start_date && item.end_date 
					? String(moment(item.end_date).diff(moment(item.start_date), 'days'))
					: String(item.request_active_days || '3'),
				currency: item.services?.unit || 'EUR',
			};
		});
		
		return {
			data: mappedData,
			pagination: {
				total_pages: response.data.data.totalPages,
				current_page: response.data.data.currentPage,
				total_items: response.data.data.totalData,
			},
		};
	} catch (error: any) {
		console.error('Error fetching offers:', error);
		throw error;
	}
};

// Update getRequestDetailByIdParallel to also use the parallel version
export const getRequestDetailByIdParallel = async (requestId: string): Promise<ExpressOffer> => {
	try {
		// 1. First, get the main request details
		const requestResponse = await http.get<RequestDetailResponse>(
			'requests',
			`specialist-request/by-id/${requestId}`
		);

		if (!requestResponse.data.success) {
			throw new Error(requestResponse.data.error || 'Failed to fetch request details');
		}

		const requestData = requestResponse.data.data;

		// 2. Create parallel requests for user, service, crop, language data, and channelId
		const [userResponse, serviceResponse, cropsResponse, languagesResponse] = await Promise.all([
			http.get<UserDataResponse>('users', `request-user/${requestData.farmer_id}`),
			http.get<ServiceDataResponse>(
				'business-services',
				`list/get-by-id/?ids=${requestData.services_id}`
			),
			http.get<CropDataResponse>('crops', 'list/'),
			http.get<LanguageDataResponse>('languages', 'list/'),
		]);

		// 3. Process the data
		const userData = userResponse.data.data;
		const serviceData = serviceResponse.data.data;

		// Find the crop data by crop_id
		const allCrops = cropsResponse.data.data;
		const cropData = allCrops.find((crop) => crop.id === requestData.crop_id) || {
			id: requestData.crop_id,
			name: 'Unknown Crop',
			image_url: '',
			country_code: 'en',
		};

		// Find the language data from language IDs
		const allLanguages = languagesResponse.data.data;
		const languageData = requestData.communication_language.map((langId) => {
			const language = allLanguages.find((lang) => lang.id === langId);
			return language || { id: langId, name: 'Unknown Language' };
		});

		// 4. Transform the data to match ExpressOffer structure
		const expressOffer: ExpressOffer = {
			id: requestData.id,
			f100_url: '',
			crops: [
				{
					id: cropData.id,
					name: cropData.name,
					image_url: cropData.image_url,
					country_code: cropData.country_code,
				},
			],
			farmer: {
				id: userData.id,
				user_id: userData.user_id,
				full_name: `${userData.first_name} ${userData.last_name}`,
				profile_image_url: userData.profile_image,
				country: {
					name: userData.country,
					alpha_two: userData.unicode?.alpha_two || '',
					alpha_three: userData.unicode?.alpha_three || '',
				},
			},
			services: serviceData.map((service) => ({
				id: service.id,
				name: service.name,
				price: service.price,
				duration: service.duration,
			})),
			total_price: serviceData.reduce((sum, service) => sum + service.price, 0),
			currency: serviceData[0]?.currency || 'EUR',
			farm_details: {
				area: requestData.area,
				unit: requestData.unit,
				location: {
					address: requestData.location_name,
					latitude: requestData.latitude,
					longitude: requestData.longitude,
				},
			},
			communication_languages: languageData,
			comment: requestData.problem_description,
			request_valid_for_days: requestData.start_date && requestData.end_date 
				? String(moment(requestData.end_date).diff(moment(requestData.start_date), 'days'))
				: '5', // Calculate days between start_date and end_date
			request_valid_for_timestamp: requestData.end_date,
			service_delivery_days: '5', // Default or calculate from dates
			service_delivery_timestamp: requestData.start_date,
			offer_id: requestData.offer_id,
			payment_status: requestData.payment_status, // Add payment status
			is_paid: requestData.payment_status === 'paid' || requestData.payment_status === 'stripe_payed', // Add is_paid flag
			attachments: requestData.request_files,
			// Request ID and farmer ID needed for channel ID lookup
			request_id: requestData.request_id,
			// Add any other required fields
		};

		// 5. Get and add channel ID
		try {
			// Import the chatService directly to avoid circular dependencies
			const { chatService } = await import('services/chat/chatService');
			const channelId = await chatService.getChannelIdByRequestAndUserId(
				requestData.request_id,
				userData.user_id
			);
			expressOffer.channelId = channelId || '';
		} catch (error) {
			console.error(`Error fetching channelId for request ${requestData.id}:`, error);
			expressOffer.channelId = '';
		}

		return expressOffer;
	} catch (error: any) {
		console.error('Error fetching combined request details:', error);
		throw error;
	}
};

// Create offer API
export const createOffer = async (offerData: {
	farmer_id: string;
	specialist_id: string;
	request_id: string;
	price: string;
	currency: string;
	is_free: boolean;
	is_default_price: boolean;
	consultation_type: string;
	offer_status: string;
	offer_comment: string;
}): Promise<{ success: boolean; message: string; offerId?: string }> => {
	try {
		const response = await http.post<any>('offers', 'create-offer', offerData);

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to create offer');
		}

		// Assume the response data can be of any shape
		const responseData = response.data.data as any;

		return {
			success: true,
			message: 'Offer created successfully',
			offerId: responseData?.offer_id || responseData?.id || '',
		};
	} catch (error: any) {
		console.error('Error creating offer:', error);
		throw new Error(error.response?.data?.error || error.message || 'Failed to create offer');
	}
};

export const sendOffer = async (
	offerId: string,
	offerData: SendOfferRequest
): Promise<{ success: boolean; message: string }> => {
	try {
		const response = await http.post(MODULE_NAME, `offers/${offerId}/send`, offerData);
		return {
			success: true,
			message: 'Offer sent successfully',
		};
	} catch (error: any) {
		console.error('Error sending offer:', error);
		throw error;
	}
};

export const cancelOffer = async (
	offerId: string
): Promise<{ success: boolean; message: string }> => {
	try {
		const response = await http.delete<APIResponse>('offers', `cancel-offer/${offerId}`);

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to cancel offer');
		}

		return {
			success: true,
			message: 'Offer cancelled successfully',
		};
	} catch (error: any) {
		console.error('Error cancelling offer:', error);
		throw new Error(error.response?.data?.error || error.message || 'Failed to cancel offer');
	}
};
